import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Check, AlertCircle, Loader, Settings } from 'lucide-react';
import pushService from '../../services/pushNotifications';

const PushNotificationManager = ({ variant = 'full' }) => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const isSupported = pushService.isPushSupported();
      setSupported(isSupported);

      if (isSupported) {
        setPermission(pushService.getPermissionStatus());
        const isSub = await pushService.isSubscribed();
        setSubscribed(isSub);

        // Register service worker
        await pushService.registerServiceWorker();
      }
    } catch (err) {
      console.error('Push status check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);

    try {
      await pushService.subscribeToPush();
      setSubscribed(true);
      setPermission('granted');
    } catch (err) {
      console.error('Subscribe error:', err);
      setError(err.message);
      if (err.message.includes('permission')) {
        setPermission('denied');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    setError(null);

    try {
      await pushService.unsubscribeFromPush();
      setSubscribed(false);
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Compact variant for navbar/settings
  if (variant === 'compact') {
    if (!supported) return null;

    return (
      <button
        onClick={subscribed ? handleUnsubscribe : handleSubscribe}
        disabled={loading || permission === 'denied'}
        className={`p-2 rounded-lg transition-colors ${
          subscribed
            ? 'bg-green-600/20 text-green-500 hover:bg-green-600/30'
            : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
        } disabled:opacity-50`}
        title={subscribed ? 'Notifications enabled' : 'Enable notifications'}
      >
        {loading ? (
          <Loader size={20} className="animate-spin" />
        ) : subscribed ? (
          <Bell size={20} />
        ) : (
          <BellOff size={20} />
        )}
      </button>
    );
  }

  // Full variant for settings page or modal
  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 p-6" data-testid="push-notification-manager">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-3 rounded-lg ${subscribed ? 'bg-green-600/20' : 'bg-gray-800'}`}>
          {subscribed ? (
            <Bell className="text-green-500" size={24} />
          ) : (
            <BellOff className="text-gray-500" size={24} />
          )}
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Push Notifications</h3>
          <p className="text-gray-400 text-sm">
            {subscribed
              ? 'You will receive notifications about new content and events'
              : 'Enable notifications to stay updated'}
          </p>
        </div>
      </div>

      {!supported && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle size={18} />
          Push notifications are not supported in your browser
        </div>
      )}

      {permission === 'denied' && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertCircle size={18} />
          <div>
            <p className="font-medium">Notifications blocked</p>
            <p className="text-sm">Please enable notifications in your browser settings</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {supported && permission !== 'denied' && (
        <div className="space-y-3">
          {subscribed ? (
            <>
              <div className="flex items-center gap-2 text-green-400 bg-green-500/10 px-4 py-3 rounded-lg">
                <Check size={18} />
                Notifications are enabled
              </div>
              <button
                onClick={handleUnsubscribe}
                disabled={loading}
                className="w-full bg-gray-800 hover:bg-gray-700 text-white py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <Loader size={18} className="animate-spin" />
                ) : (
                  <>
                    <BellOff size={18} />
                    Disable Notifications
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader size={18} className="animate-spin" />
              ) : (
                <>
                  <Bell size={18} />
                  Enable Notifications
                </>
              )}
            </button>
          )}
        </div>
      )}

      {subscribed && (
        <div className="mt-4 pt-4 border-t border-gray-800">
          <p className="text-gray-500 text-sm mb-2">You'll be notified about:</p>
          <ul className="text-gray-400 text-sm space-y-1">
            <li>• New movie and TV show releases</li>
            <li>• Live stream announcements</li>
            <li>• PPV event reminders</li>
            <li>• Weekly content digests</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default PushNotificationManager;
